import { PublicClientApplication, LogLevel } from '@azure/msal-browser'
import type { Configuration, RedirectRequest } from '@azure/msal-browser'
import type { RuntimeConfig } from './config'

let instance: PublicClientApplication | null = null
let request: RedirectRequest | null = null

function msalConfig(config: RuntimeConfig): Configuration {
  return {
    auth: {
      clientId: config.entraClientId,
      authority: `https://login.microsoftonline.com/${config.entraTenantId}`,
      redirectUri: window.location.origin,
      postLogoutRedirectUri: window.location.origin,
    },
    cache: {
      // Same trade-off as Prism: localStorage keeps the session across tabs
      // and refreshes, and tokens survive XSS until they expire. The shell runs
      // no third-party script, and dashboards (the only untrusted HTML) run in
      // an opaque-origin frame that cannot read this storage (ADR-002).
      cacheLocation: 'localStorage',
    },
    system: {
      loggerOptions: {
        loggerCallback: (level: LogLevel, message: string, containsPii: boolean) => {
          if (containsPii) return // Never log PII
          if (import.meta.env.DEV) console.warn(`MSAL [${LogLevel[level]}]: ${message}`)
        },
        logLevel: import.meta.env.DEV ? LogLevel.Warning : LogLevel.Error,
      },
    },
  }
}

/** Creates and initialises MSAL once the runtime config is known. */
export async function initAuth(config: RuntimeConfig): Promise<PublicClientApplication> {
  instance = new PublicClientApplication(msalConfig(config))
  request = { scopes: ['openid', 'profile', 'email', config.apiScope] }
  await instance.initialize()
  const result = await instance.handleRedirectPromise()
  if (result?.account) instance.setActiveAccount(result.account)
  return instance
}

export function getMsal(): PublicClientApplication {
  if (!instance) throw new Error('MSAL not initialised')
  return instance
}

export function getLoginRequest(): RedirectRequest {
  if (!request) throw new Error('MSAL not initialised')
  return request
}
